import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Text,
  Link,
  Flex,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Signup = () => {
  const { signup } = useAuth();

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false); // 👈 New success state

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let score = 0;
    if (!password) return { score, label: 'Too Short' };

    if (password.length >= 6) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[!@#$%^&*()_\-+=]/.test(password)) score += 1;

    let label = 'Weak';
    if (score >= 4) label = 'Strong';
    else if (score >= 2) label = 'Medium';
    return { score, label };
  };

  const strength = useMemo(() => getPasswordStrength(form.password), [form.password]);

  const getStrengthColor = (score) => {
    switch (strength.label) {
      case 'Too Short': return 'gray.300';
      case 'Weak': return 'red.400';
      case 'Medium': return 'yellow.400';
      case 'Strong': return 'green.400';
      default: return 'gray.300';
    }
  };

  const validateForm = () => {
    if (!form.email) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setError('Email format is invalid');
      return false;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      await signup(form.email, form.password);
      setSuccess(true); // 👈 Show confirmation message instead of navigating
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Show success message instead of form
  if (success) {
    return (
      <Box px={{ base: 4, md: 0 }} py={{ base: 6, md: 12 }}>
        <Box className="container">
          <Box maxW="400px" mx="auto" mt={{ base: 6, md: 24 }} p="6" borderWidth="1px" borderRadius="lg" boxShadow="md" textAlign="center">
        <Alert
          status="success"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="md"
          mb={6}
        >
          <AlertIcon boxSize="30px" mr={0} />
          <AlertTitle mt={2} mb={1} fontSize="lg">
            Verification Email Sent!
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            Please check your inbox (and spam folder) to verify your email before logging in.
          </AlertDescription>
        </Alert>

        <Button as={RouterLink} to="/login" colorScheme="teal" width="full">
          Go to Login
        </Button>
      </Box>
    </Box>
  </Box>
    );
  }

  return (
      <Box px={{ base: 4, md: 0 }} py={{ base: 6, md: 12 }}>
        <Box className="container">
          <Box maxW="400px" mx="auto" mt={{ base: 6, md: 24 }} p="6" borderWidth="1px" borderRadius="lg" boxShadow="md">
            <Heading mb="6" textAlign="center">Sign Up</Heading>

      {error && (
        <Alert status="error" mb="4" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <VStack spacing="4">
          <FormControl id="email" isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
            />
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Enter password"
            />
            {form.password.length > 0 && (
              <Box mt={2}>
                <Text fontSize="sm" mb={1}>Strength: {strength.label}</Text>
                <Flex h="6px" borderRadius="md" overflow="hidden" bg="gray.200">
                  <Box
                    w={`${(strength.score / 5) * 100}%`}
                    bg={getStrengthColor(strength.score)}
                    transition="width 0.3s"
                  />
                </Flex>
              </Box>
            )}
          </FormControl>

          <FormControl id="confirmPassword" isRequired>
            <FormLabel>Confirm Password</FormLabel>
            <Input
              type="password"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm password"
            />
          </FormControl>

          <Button colorScheme="teal" width="full" type="submit" isDisabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Sign Up'}
          </Button>
        </VStack>
      </form>

      <Text mt={4} textAlign="center">
        Already have an account?{' '}
        <Link as={RouterLink} to="/login" color="teal.500" fontWeight="bold">
          Log in
        </Link>
      </Text>
          </Box>
        </Box>
      </Box>
  );
};

export default Signup;
